import get from 'lodash/get';
import set from 'lodash/set';
import {
  getFieldPreRequisites,
  getLegacyPreRequisiteFields,
  preRequisitesCheck,
} from '@components/common/formGroup/preRequisites';
import { checkConditions } from '@components/common/formGroup/conditionsEvaluator';
import type { ConditionsConfig } from '@components/common/formGroup/conditionsEvaluator';
import {
  Field,
  FormData,
  PreRequisiteField,
  PreRequisites,
  RowFields,
} from '@components/common/formSchema/types';

/**
 * Evaluates field visibility using the new conditions system.
 * @returns shouldHide - true if field should not be rendered
 * @returns forceDisabled - true if field should be shown but disabled
 */
const evaluateNewConditionsSystem = (
  conditionsConfig: ConditionsConfig,
  formData: FormData,
  availableFeatureFlags: Record<string, boolean>,
): { shouldHide: boolean; forceDisabled: boolean } => {
  const { shouldShow, shouldDisable } = checkConditions(conditionsConfig, {
    formData,
    availableFeatureFlags,
  });

  return {
    shouldHide: !shouldShow,
    forceDisabled: shouldDisable,
  };
};

/**
 * Evaluates field visibility using the legacy preRequisites system.
 * @returns shouldHide - true if field should not be rendered
 * @returns forceDisabled - true if field should be shown but disabled
 */
const evaluateLegacyPreRequisites = (
  field: Field | RowFields,
  formData: FormData,
  availableFeatureFlags: Record<string, boolean>,
): { shouldHide: boolean; forceDisabled: boolean } => {
  const preRequisites: PreRequisites = {
    ...(field.preRequisites ?? {}),
    fields: getFieldPreRequisites(field),
  };
  const hasPreRequisites = (preRequisites.fields as PreRequisiteField[]).length > 0;
  const hasPreReqFeatureFlags = preRequisites.featureFlags && preRequisites.featureFlags.length > 0;

  if (hasPreRequisites || hasPreReqFeatureFlags) {
    const isPreRequisitesSatisfied = preRequisitesCheck(
      preRequisites,
      formData,
      availableFeatureFlags,
    );

    if (!isPreRequisitesSatisfied) {
      /* disabledMode
        if true this field will appear as disabled until the preRequisiteField has a value
        if false or not set the field will not appear in the ui until the preRequisiteField has a value
      */
      if (preRequisites.disabledMode) {
        return { shouldHide: false, forceDisabled: true };
      }
      return { shouldHide: true, forceDisabled: false };
    }
  }

  return { shouldHide: false, forceDisabled: false };
};

export const getFieldConfigs = (
  field: Field | RowFields,
  formData: FormData,
  availableFeatureFlags: Record<string, boolean> = {},
) => {
  let forceDisabled = false;
  let preRequisiteValue;
  let preRequisiteObjectValue;

  const conditionsConfig = field.conditions;

  const result = conditionsConfig
    ? evaluateNewConditionsSystem(conditionsConfig, formData, availableFeatureFlags)
    : evaluateLegacyPreRequisites(field, formData, availableFeatureFlags);

  if (result.shouldHide) {
    return null;
  }

  forceDisabled = result.forceDisabled;

  // Handle legacy preRequisiteField includeValue & includeToRequest logic
  // (only relevant for old preRequisites system)
  if (!conditionsConfig) {
    // using only the first legacy preRequisiteField for reading includeValue & includeToRequest values
    const firstLegacyPreRequisiteField = getLegacyPreRequisiteFields(
      (field as Field).preRequisiteField,
    )[0];

    if (firstLegacyPreRequisiteField && firstLegacyPreRequisiteField.includeValue) {
      preRequisiteValue = get(formData, firstLegacyPreRequisiteField.name) as string | undefined;
    }

    if (firstLegacyPreRequisiteField && firstLegacyPreRequisiteField.includeToRequest) {
      const { includeToRequest } = firstLegacyPreRequisiteField;

      if (typeof includeToRequest === 'boolean') {
        preRequisiteObjectValue = set({}, firstLegacyPreRequisiteField.name, preRequisiteValue);
      } else if (typeof includeToRequest === 'object' && includeToRequest?.transformKey) {
        preRequisiteObjectValue = set({}, includeToRequest.transformKey, preRequisiteValue);
      }
    }
  }

  if ((field as Field).readOnly) {
    forceDisabled = true;
  }

  return { forceDisabled, preRequisiteValue, preRequisiteObjectValue };
};
